<template>
  <Dialog
    v-model:visible="value"
    class="base-dialog"
    :class="{ 'no-padding': noPadding }"
    :style="{ maxWidth: maxWidth }"
    modal
    :draggable="false"
    @hide="$emit('onClose')"
  >
    <template #header>
      <div class="toolbar">
        <span>{{ title }}</span>
      </div>
    </template>

    <div class="content">
      <slot />
    </div>

    <template v-if="hasSlotFooter" #footer>
      <div class="footer">
        <slot name="footer" />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps, useSlots } from 'vue'
import Dialog from 'primevue/dialog'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: null
  },
  noPadding: Boolean,
  maxWidth: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['onOpen', 'onClose', 'update:modelValue'])

const value = computed({
  get: () => props.modelValue,
  set: (val: any) => emit('update:modelValue', val)
})

const hasSlotFooter = computed(() => !!useSlots().footer)

const open = () => {
  value.value = true
  emit('onOpen')
}

function close() {
  value.value = false
  emit('onClose')
}

defineExpose({ open, close })
</script>

<style lang="scss">
// El dialog no está en el DOM, necesita estilos globales
.base-dialog.p-dialog {
  box-shadow: 5px 5px 10px 0px #02123740;
  border-radius: 12px;

  &.no-padding {
    .p-dialog-content {
      padding: 0;
    }

    .p-dialog-footer {
      padding: 0;
    }
  }

  .p-dialog-header {
    background: var(--vx-dialog-header);
    color: #ffffff;
    max-height: 48px;

    .toolbar {
      justify-content: center;
    }

    .p-dialog-header-close {
      color: #ffffff;
    }
    .p-dialog-header-icon {
      &:hover {
        background-color: rgba($vx-accent, 0.32);
      }
    }
  }

  .p-dialog-content {
    padding: 1.5rem;
    background-color: #f9f9fb;
  }

  .p-dialog-footer {
    padding: 1rem;
    button {
      margin-right: 0;
      margin-left: 0.5rem;
    }
  }
}
</style>

<style lang="scss" scoped>
.base-dialog {
  .toolbar {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  .content {
    width: auto;
    color: var(--vx-primary);

    .section {
      display: flex;
      flex-direction: column;
    }

    .title {
      font-size: 1rem;
      font-weight: 600;
    }

    .subtitle {
      font-size: 0.875rem;
      font-weight: 400;
    }

    :deep(.p-inputtext) {
      width: 100%;
    }
  }

  .footer {
    color: var(--vx-primary);
    display: flex;
    &:empty {
      display: none;
    }
  }
}
</style>
